import { createFileRoute, useSearch } from '@tanstack/react-router'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import {
  ClientPortalSidebar,
  type ClientPortalSection,
} from '@/components/settings/client-portal-sidebar'
import { GallerySection } from '@/components/settings/client-portal/gallery-section'
import { PortalDomainSection } from '@/components/settings/client-portal/portal-domain-section'
import { TermsSection } from '@/components/settings/client-portal/terms-section'
import { TipSettingsSection } from '@/components/settings/client-portal/tip-settings-section'
import { useCurrentOrganization } from '@/stores/organization.store'
import { SkeletonBar } from '@/components/shared/skeleton-bar'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { requirePermission, PermissionDeniedRoute } from '@/lib/require-permission-guard'

type ClientPortalSearch = {
  section?: ClientPortalSection
}

export const Route = createFileRoute('/_app/_settings/client-portal')({
  component: ClientPortalPage,
  validateSearch: (search: Record<string, unknown>): ClientPortalSearch => {
    return {
      section: (search.section as ClientPortalSection) || 'domain',
    }
  },
  beforeLoad: async ({ context }) => {
    await requirePermission(context.queryClient, 'SettingsManage', 'client portal settings')
  },
  errorComponent: PermissionDeniedRoute,
})

function ClientPortalPage() {
  useBreadcrumbFromRoute()
  const search = useSearch({ from: '/_app/_settings/client-portal' })
  const currentSection = search.section || 'domain'
  const { currentOrganization, isLoading, refetch } = useCurrentOrganization()

  return (
    <div className="flex h-full overflow-hidden">
      <div className="scroll-fade-y flex-shrink-0 overflow-y-auto">
        <ClientPortalSidebar />
      </div>
      <div className="flex flex-1 flex-col overflow-y-auto">
        <div className="flex flex-col gap-4 p-6">
          {isLoading ? (
            <LoadingSkeleton />
          ) : currentOrganization ? (
            <>
              {currentSection === 'domain' && (
                <PortalDomainSection organization={currentOrganization} />
              )}
              {currentSection === 'gallery' && <GallerySection organization={currentOrganization} />}
              {currentSection === 'terms' && <TermsSection />}
              {currentSection === 'tips' && (
                <TipSettingsSection organization={currentOrganization} onRefetch={refetch} />
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground">No organization selected</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div>
        <SkeletonBar className="h-8 w-64 mb-2" />
        <SkeletonBar className="h-4 w-96" />
      </div>
      <Card>
        <CardHeader>
          <SkeletonBar className="h-6 w-48" />
          <SkeletonBar className="h-4 w-72" />
        </CardHeader>
        <CardContent className="space-y-4">
          <SkeletonBar className="h-10 w-full" />
          <SkeletonBar className="h-10 w-full" />
          <SkeletonBar className="h-10 w-full" />
        </CardContent>
      </Card>
    </div>
  )
}
